/* eslint-disable @typescript-eslint/no-explicit-any */
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Checkbox from '@mui/material/Checkbox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import type {
  GridColDef,
  GridRowId,
  GridRowModesModel,
} from '@mui/x-data-grid';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import React, { useContext } from 'react';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { TableName } from '../../../constants/table';
import {
  deleteEntity,
  getByPartitionKey,
  checkTableForReference,
} from '../../../lib/services/api-service';
import { useGetAllCompanyMasters } from '../../../lib/query/companyMaster';
import TenantContext from '../../../lib/context/TenantContext';

export const columns = (
  rowModesModel: GridRowModesModel,
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>,
  onManageMappings: (
    companyId: string,
    companyName: string,
    companyGst: string,
  ) => void,
): GridColDef[] => {
  const { tenant } = useContext(TenantContext);
  const { refetch } = useGetAllCompanyMasters(tenant.id ?? '');

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDelete =
    (partitionKey: string, companyId: string, companyName: string) =>
    async () => {
      // Find the row to check if it's active
      // Note: Row data is passed via closure or we can find it if needed. 
      // In this grid, the delete button is tylko for the "Master" row.
      
      try {
        // Fetch the master record to verify status (backup check)
        const allMasters = await getByPartitionKey(TableName.CompanyMaster, partitionKey);
        const master = allMasters.find((m: any) => m.RowKey === companyId);
        
        if (master?.Active === true) {
          toast.warn(
            'Cannot delete an active company. Please mark it as inactive first.',
          );
          return;
        }

        // Check for active references
        const hasActiveReferences = await checkActiveReferences(
          partitionKey,
          companyId,
        );
        if (hasActiveReferences) {
          toast.warn(
            'Unable to delete company due to active ledger/stock references.',
          );
          return;
        }

        // 1. Fetch all mappings to find those linked to this company
        const allMappings = await getByPartitionKey(
          TableName.CompanyMapping,
          partitionKey,
        );

        const companyMappings = allMappings.filter(
          (mapping: any) => mapping.CompanyId === companyId,
        );

        // 2. Delete all mappings
        const mappingDeletePromises = companyMappings.map((mapping: any) =>
          deleteEntity(
            TableName.CompanyMapping,
            partitionKey,
            mapping.RowKey,
          ).catch(e => {
            console.error(
              `Failed to delete mapping ${mapping.RowKey}: ${e.message}`,
            );
          }),
        );

        // 3. Delete the CompanyMaster record and all mappings together
        await Promise.all([
          deleteEntity(TableName.CompanyMaster, partitionKey, companyId).catch(e => {
            throw new Error(`Failed to delete from CompanyMaster: ${e.message}`);
          }),
          ...mappingDeletePromises,
        ]);

        await refetch();
        toast.success(
          `Deleted Company "${companyName}" and ${companyMappings.length} associated mapping(s)`,
        );
      } catch (error: any) {
        console.error('Error deleting company:', error);
        toast.error(`Failed to delete company: ${error.message}`);
      }
    };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const checkActiveReferences = async (
    partitionKey: string,
    CompanyId: string,
  ): Promise<boolean> => {
    try {
      const defaultLedgerReference = await checkTableForReference(
        TableName.DefaultLedger,
        partitionKey,
        CompanyId,
      );
      const commonCodeReference = await checkTableForReference(
        TableName.CommonCode,
        partitionKey,
        CompanyId,
      );
      const stockMasterReference = await checkTableForReference(
        TableName.StockMaster,
        partitionKey,
        CompanyId,
      );

      const hasActiveReferences =
        defaultLedgerReference || commonCodeReference || stockMasterReference;
      return hasActiveReferences;
    } catch (error) {
      console.error('Error checking for active references:', error);
      return true;
    }
  };

  return [
    {
      field: 'RowKey',
      headerName: 'CompanyId',
      type: 'string',
      flex: 1,
      headerClassName: 'gridHeader--header',
      hideable: true,
      renderCell: params => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(params.value);
            toast.info(`Copied Company Id: ${params.value}`);
          }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'CompanyOCR',
      headerName: 'CompanyOCR',
      type: 'string',
      flex: 1,
      headerClassName: 'gridHeader--header',
    },
    {
      field: 'CompanyName',
      headerName: 'CompanyName',
      type: 'string',
      editable: true,
      flex: 1,
      headerClassName: 'gridHeader--header',
    },
    {
      field: 'CompanyGst',
      headerName: 'CompanyGST',
      type: 'string',
      flex: 1,
      headerClassName: 'gridHeader--header',
    },
    {
      field: 'Active',
      headerName: 'Active',
      type: 'boolean',
      editable: true,
      renderCell: params => (
        <Checkbox
          checked={params.value === true}
          color="primary"
          inputProps={{ 'aria-label': 'status checkbox' }}
          disabled={params.value === false}
        />
      ),
      flex: 1,
      headerClassName: 'gridHeader--header',
    },

    {
      field: 'ExportFormat',
      headerName: 'ExportFormat',
      width: 150,
      renderCell: (params: any) => {
        // Display label in normal mode
        const formatMap: { [key: number]: string } = {
          1: 'Tally',
          2: 'Zoho',
          3: 'Busy',
        };
        const value = params.value ?? params.row?.ExportFormat;
        return formatMap[value] || value;
      },
      renderEditCell: (params: any) => {
        // Show dropdown in edit mode
        const value = params.value ?? params.row?.ExportFormat;
        return (
          <select
            value={value}
            onChange={e =>
              params.api.setEditCellValue({
                id: params.id,
                field: 'ExportFormat',
                value: Number(e.target.value),
              })
            }
            style={{ width: '100%' }}>
            <option value={1}>Tally</option>
            <option value={2}>Zoho</option>
            <option value={3}>Busy</option>
          </select>
        );
      },
      editable: true,
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      cellClassName: 'actions',
      flex: 1,
      headerClassName: 'gridHeader--header',
      getActions: (params: {
        row: {
          id: GridRowId;
          PartitionKey: string;
          RowKey: string;
          CompanyOCR: string;
          CompanyName: string;
          CompanyGst: string;
          Active?: boolean;
        };
      }) => {
        const { row } = params;
        const { PartitionKey } = row;
        const { RowKey } = row;
        const isInEditMode = rowModesModel[row.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              key="save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(row.id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              key="cancel"
              className="textPrimary"
              onClick={handleCancelClick(row.id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            key="edit"
            className="textPrimary"
            onClick={handleEditClick(row.id)}
            color="inherit"
          />,
          <Tooltip title="View Mappings" key="view-mappings">
            <GridActionsCellItem
              icon={<ListAltIcon />}
              label="View Mappings"
              onClick={() =>
                onManageMappings(RowKey, row.CompanyName, row.CompanyGst)
              }
              color="inherit"
            />
          </Tooltip>,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            key="delete"
            label="Delete"
            onClick={handleDelete(
              PartitionKey,
              RowKey,
              row.CompanyName,
            )}
            color="inherit"
          />,
        ];
      },
    },
  ];
};
